import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import ReactDom from 'react-dom';
import 'mobx-react/batchingForReactDom';
import { globalInfoStore, globalInfoStoreContext } from '@/models/global';
import '@/styles/reset.scss';
import { observer } from 'mobx-react';
import { ConfigProvider } from '@byte-design/ui';
import zhCN from '@byte-design/locale/es/zh_CN';
import enGB from '@byte-design/locale/es/en_GB';
import { useGlobalLoadingEffect } from '@/utils/hooks/common';
import { initI18Next, loadForLang } from './starling';
import i18next from 'i18next';
import 'antd/lib/select/style';
import 'antd/lib/tree-select/style';
import 'antd/lib/auto-complete/style';
import GLoading from '@/component/Loading';
import { initDataFinder } from './logger/DataFinder';
import '@/utils/copy'; // 单独引入防止多次执行
import Entry from '@/component/Entry';
import slardarInit from './slardarInit';

import '@icon-park/react/styles/index.css';

export interface AppWrapperProps<T> {
  Component: React.FunctionComponent;
  baseName: string;
  onReady?: () => void;
  spaContext?: React.Context<T>;
  spaStore?: T;
  title?: string;
}

/* eslint-disable no-undef,no-console,func-names */
console.log(`%c 当前版本: ${BUILD_VERSION || '本地开发'} \n 环境模式: ${ZONE}`, 'font-size:18px;color:red;');

const AppWrapperFunc = function <T = any>(props: AppWrapperProps<T>) {
  const {
    Component, spaStore, spaContext, baseName, title = '',
    onReady,
  } = props;

  // app ready
  const [ready, setReady] = useState(false);

  const [, setupLocale] = useGlobalLoadingEffect(async () => {
    const userStoredLocale = globalInfoStore.lng;

    await initI18Next();
    await loadForLang(userStoredLocale);

    setReady(true);

    if (onReady) onReady();
  });

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('in local');
    }
    setupLocale();
    initDataFinder();
    document.title = i18next.t(title);
  }, []);

  // init slardar实例
  useEffect(() => {
    const browserClient = slardarInit({
      extractPid: baseName,
    });
    browserClient('start');
    return () => {
      browserClient('destroy');
    };
  }, []);

  const localeForBui = globalInfoStore.lng === 'en' ? enGB : zhCN;

  const inner = (
    <BrowserRouter basename={baseName}>
      {ready && <Component />}
      <GLoading />
      <Entry />
    </BrowserRouter>
  );

  const outer = spaStore && spaContext ? (
    <spaContext.Provider value={spaStore}>
      {inner}
    </spaContext.Provider>
  ) : (<>{inner}</>);

  return (
    <globalInfoStoreContext.Provider value={globalInfoStore}>
      <ConfigProvider locale={localeForBui}>
        {outer}
      </ConfigProvider>
    </globalInfoStoreContext.Provider>
  );
};

export const AppWrapper = observer(AppWrapperFunc);

export async function renderApp(app: React.ReactElement, container: HTMLElement) {
  ReactDom.render(app, container);
}
